import React from 'react';
import { cn } from '@/lib/cn';
import { IconInfo } from '@repo/svgs';

export type TextBadgeVariant = 'neutral' | 'positive' | 'negative' | 'caution';

export interface TextBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The variant determines the color scheme */
  variant?: TextBadgeVariant;
  /** The text content to display */
  children: React.ReactNode;
  /** Optional icon to display. Defaults to IconInfo */
  icon?: React.ReactNode;
  /** Whether to show the default icon */
  showIcon?: boolean;
  /** Optional override for the text color/typography (e.g. 'text-caution') */
  textClassName?: string;
}

/**
 * TextBadge - A text-based badge component with icon and message
 * Supports neutral, positive, and negative variants
 */
export function TextBadge({
  variant = 'neutral',
  children,
  icon,
  showIcon = true,
  textClassName,
  className,
  ...props
}: TextBadgeProps) {
  // Get variant-specific styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'positive':
        return {
          container: 'bg-success-low',
          text: 'text-success',
          iconColor: 'icon-success',
        };
      case 'negative':
        return {
          container: 'bg-alert-low',
          text: 'text-alert',
          iconColor: 'icon-alert',
        };
      case 'caution':
        return {
          container: 'bg-caution-low',
          text: 'text-caution',
          iconColor: 'icon-caution',
        };
      default: // neutral
        return {
          container: 'bg-secondary',
          text: 'text-primary',
          iconColor: 'icon-primary',
        };
    }
  };

  const styles = getVariantStyles();

  // Default icon if none provided
  const defaultIcon = <IconInfo className={cn('w-4 h-4', styles.iconColor)} />;
  const displayIcon = icon
    ? React.cloneElement(icon as React.ReactElement<any>, {
        className: cn('w-4 h-4', styles.iconColor),
      })
    : showIcon
      ? defaultIcon
      : null;

  return (
    <div
      className={cn(
        // Base container styles
        'flex items-center gap-2 px-3 py-2 rounded-xl',
        // Variant-specific background
        styles.container,
        className,
      )}
      {...props}
    >
      {/* Icon */}
      {displayIcon && <div className="flex-shrink-0">{displayIcon}</div>}

      {/* Text content */}
      <p className={cn('text-body-2 flex-1 min-w-0', styles.text, textClassName)}>{children}</p>
    </div>
  );
}

export default TextBadge;
